// services/videoThumbnailService.ts - Extract and manage video thumbnails

import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';

export class VideoThumbnailService {
  private static readonly THUMBNAIL_MAX_WIDTH = 1920;
  private static readonly THUMBNAIL_JPEG_QUALITY = 0.97;
  private static readonly SMART_CAPTURE_RATIO = 0.25;
  private static readonly SMART_CAPTURE_MAX_SECONDS = 2;

  /**
   * Extract a thumbnail from a video file at a specific time
   * @param videoFile - The video file to extract from
   * @param timeInSeconds - Time position to capture (default: 1 second)
   * @returns Promise<Blob> - The thumbnail as a blob
   */
  static async extractThumbnail(
    videoFile: File | string,
    timeInSeconds: number = 1
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      console.log('🟡 Creating video element for thumbnail extraction...');
      
      // Create video element
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.preload = 'metadata';
      video.muted = true; // Mute to avoid autoplay issues
      video.playsInline = true; // iOS: decode frames inline instead of forcing the fullscreen player
      let objectUrl: string | null = null;
      
      // Create canvas for drawing frame
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      let hasResolved = false;

      video.onloadedmetadata = () => {
        console.log('🟡 Video metadata loaded. Duration:', video.duration, 'seconds');
        
        // Set canvas size to video dimensions (or scale down)
        const maxWidth = VideoThumbnailService.THUMBNAIL_MAX_WIDTH;
        const scale = Math.min(1, maxWidth / video.videoWidth);
        canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
        canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
        
        console.log('🟡 Canvas size:', canvas.width, 'x', canvas.height);
        
        // If timeInSeconds is 0, use smart capture (25% into video or 2 seconds, whichever is less).
        const fallbackDuration = Number.isFinite(video.duration) ? video.duration : 0;
        const maxSeekTime = Math.max(fallbackDuration - 0.05, 0);
        const smartCaptureTime = Math.min(
          fallbackDuration * VideoThumbnailService.SMART_CAPTURE_RATIO,
          VideoThumbnailService.SMART_CAPTURE_MAX_SECONDS
        );
        const requestedSeekTime = timeInSeconds > 0 ? timeInSeconds : smartCaptureTime;
        const seekTime = Math.min(Math.max(requestedSeekTime, 0), maxSeekTime);
        console.log('🟡 Seeking to', seekTime, 'seconds...');
        video.currentTime = seekTime;
      };

      video.onseeked = () => {
        if (hasResolved) return; // Prevent double resolution
        hasResolved = true;
        
        console.log('🟡 Video seeked successfully. Drawing frame to canvas...');
        
        try {
          // Draw the current frame to canvas
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          console.log('🟡 Frame drawn. Converting to blob...');
          
          // Convert canvas to blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                console.log('🟢 Thumbnail blob created. Size:', blob.size, 'bytes');
                // Clean up
                if (objectUrl) {
                  URL.revokeObjectURL(objectUrl);
                }
                resolve(blob);
              } else {
                console.error('❌ Failed to create blob from canvas');
                reject(new Error('Failed to create thumbnail blob'));
              }
            },
            'image/jpeg',
            VideoThumbnailService.THUMBNAIL_JPEG_QUALITY
          );
        } catch (error) {
          console.error('❌ Error drawing to canvas:', error);
          reject(error);
        }
      };

      video.onerror = (e) => {
        console.error('❌ Video error during thumbnail extraction:', e);
        reject(new Error('Failed to load video for thumbnail extraction'));
      };

      // Load the video
      console.log('🟡 Loading video file...');
      if (typeof videoFile === 'string') {
        video.src = videoFile;
      } else {
        objectUrl = URL.createObjectURL(videoFile);
        video.src = objectUrl;
      }
      
      // Timeout fallback - if nothing happens in 10 seconds, reject
      setTimeout(() => {
        if (!hasResolved) {
          console.error('❌ Thumbnail extraction timed out after 10 seconds');
          reject(new Error('Thumbnail extraction timed out'));
        }
      }, 10000);
    });
  }

  /**
   * Upload thumbnail to Firebase Storage
   * @param thumbnailBlob - The thumbnail blob
   * @param userId - User ID
   * @param videoId - Video ID
   * @returns Promise<string> - The storage path
   */
  static async uploadThumbnail(
    thumbnailBlob: Blob,
    userId: string,
    videoId: string
  ): Promise<string> {
    const thumbnailPath = `users/${userId}/thumbnails/${videoId}.jpg`;
    const thumbnailRef = ref(storage, thumbnailPath);
    
    console.log('🟡 Uploading thumbnail to Storage...');
    await uploadBytes(thumbnailRef, thumbnailBlob, {
      contentType: 'image/jpeg',
      cacheControl: 'public, max-age=31536000' // 1 year cache
    });
    console.log('🟢 Thumbnail uploaded');
    
    return thumbnailPath;
  }

  /**
   * Get thumbnail download URL
   * @param thumbnailPath - Storage path to thumbnail
   * @returns Promise<string> - Download URL
   */
  static async getThumbnailUrl(thumbnailPath: string): Promise<string> {
    const thumbnailRef = ref(storage, thumbnailPath);
    return await getDownloadURL(thumbnailRef);
  }

  /**
   * Extract and upload thumbnail in one operation
   * @param videoFile - The video file
   * @param userId - User ID
   * @param videoId - Video ID
   * @param timeInSeconds - Time position to capture (default: 0 for smart capture)
   * @returns Promise<string> - The storage path
   */
  static async extractAndUploadThumbnail(
    videoFile: File,
    userId: string,
    videoId: string,
    timeInSeconds: number = 0
  ): Promise<string> {
    console.log('🟡 Extracting thumbnail from video...');
    
    // If timeInSeconds is 0, use smart capture (25% into the video or 2 seconds, whichever is less)
    // This is calculated after we know the video duration
    const thumbnailBlob = await this.extractThumbnail(videoFile, timeInSeconds);
    console.log('🟢 Thumbnail extracted');
    
    const thumbnailPath = await this.uploadThumbnail(thumbnailBlob, userId, videoId);
    return thumbnailPath;
  }

  /**
   * Generate a thumbnail from a video URL (for loading existing videos)
   * This is useful for videos already in storage that don't have thumbnails
   * @param videoUrl - URL of the video
   * @param userId - User ID
   * @param videoId - Video ID
   * @returns Promise<string> - The storage path
   */
  static async generateThumbnailFromUrl(
    videoUrl: string,
    userId: string,
    videoId: string
  ): Promise<string> {
    const thumbnailBlob = await this.extractThumbnail(videoUrl, 0);
    const thumbnailPath = await this.uploadThumbnail(thumbnailBlob, userId, videoId);
    return thumbnailPath;
  }

  /**
   * Delete thumbnail from storage
   * @param thumbnailPath - Storage path to thumbnail
   */
  static async deleteThumbnail(thumbnailPath: string): Promise<void> {
    try {
      const thumbnailRef = ref(storage, thumbnailPath);
      const { deleteObject } = await import('firebase/storage');
      await deleteObject(thumbnailRef);
      console.log('🟢 Thumbnail deleted');
    } catch (error) {
      console.warn('⚠️ Failed to delete thumbnail:', error);
      // Don't throw - thumbnail deletion is not critical
    }
  }
}

export default VideoThumbnailService;
